import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Container, Card, Row, Col, Button, Spinner } from 'react-bootstrap';
import { selectPortalBusiness, logoutPortal } from '../../redux/reducers/portalAuthSlice';
import { LuLogOut, LuBuilding2, LuArrowRight } from 'react-icons/lu';
import ConfirmModal from '../../components/ConfirmModal';

const PortalSelectBusiness = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const { tempBusinesses, loading, portalToken, customer } = useSelector((state) => state.portalAuth);

    useEffect(() => {
        // If already authenticated with a single business, redirect directly
        if (portalToken && customer?.businessId) {
            navigate(`/customer-side/${customer.businessId}/dashboard`);
            return;
        }

        // If no temporary businesses list, redirect to login
        if (!tempBusinesses || tempBusinesses.length === 0) {
            navigate('/customer-side/login');
        }
    }, [portalToken, customer, tempBusinesses, navigate]);

    const handleSelect = (biz) => {
        const payload = {
            token: biz.token,
            customer: {
                name: biz.customerName,
                email: customer?.email || biz.token ? JSON.parse(atob(biz.token.split('.')[1])).email : '',
                businessName: biz.businessName,
                businessId: biz.businessId
            }
        };
        dispatch(selectPortalBusiness(payload));
        navigate(`/customer-side/${biz.businessId}/dashboard`);
    };

    const handleBackToLogin = () => {
        setShowLogoutModal(true);
    };

    const confirmLogout = () => {
        dispatch(logoutPortal());
        navigate('/customer-side/login');
    };

    if (!tempBusinesses) return null;

    return (
        <div className="bg-light min-vh-100 d-flex align-items-center py-5" style={{
            background: 'radial-gradient(circle at top right, rgba(40, 176, 196, 0.08), transparent 40%), radial-gradient(circle at bottom left, rgba(40, 176, 196, 0.05), transparent 45%)'
        }}>
            <Container style={{ maxWidth: '600px' }}>
                <div className="text-center mb-5">
                    <div className="d-inline-flex p-3 rounded-circle bg-white shadow-sm mb-3" style={{ border: '1px solid rgba(40, 176, 196, 0.2)' }}>
                        <LuBuilding2 size={32} style={{ color: '#28B0C4' }} />
                    </div>
                    <h2 className="fw-bold text-dark mb-2">Select a Portal</h2>
                    <p className="text-muted">Your email is registered with multiple businesses. Please choose which portal you wish to enter.</p>
                </div>

                <div className="d-flex flex-column gap-3 mb-4">
                    {tempBusinesses.map((biz) => (
                        <Card 
                            key={biz.businessId} 
                            className="border-0 shadow-sm rounded-4 overflow-hidden position-relative hover-lift cursor-pointer p-1"
                            onClick={() => handleSelect(biz)}
                            style={{
                                transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                                border: '1px solid rgba(0,0,0,0.02)'
                            }}
                        >
                            <Card.Body className="d-flex align-items-center justify-content-between p-4">
                                <div className="d-flex align-items-center gap-3">
                                    {biz.logo ? (
                                        <img 
                                            src={biz.logo} 
                                            alt={biz.businessName} 
                                            className="rounded-3 object-fit-cover shadow-sm"
                                            style={{ width: '56px', height: '56px' }}
                                        />
                                    ) : (
                                        <div className="rounded-3 d-flex align-items-center justify-content-center fw-bold text-white shadow-sm"
                                             style={{ width: '56px', height: '56px', backgroundColor: '#28B0C4', fontSize: '20px' }}>
                                            {biz.businessName.charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                    <div>
                                        <h5 className="fw-bold text-dark mb-1">{biz.businessName}</h5>
                                        <div className="text-muted small">Registered as: <span className="fw-medium text-dark">{biz.customerName}</span></div>
                                    </div>
                                </div>
                                <div className="p-2 rounded-circle bg-light d-flex align-items-center justify-content-center text-dark action-btn" style={{
                                    transition: 'all 0.2s ease',
                                    width: '38px',
                                    height: '38px'
                                }}>
                                    <LuArrowRight size={20} style={{ color: '#28B0C4' }} />
                                </div>
                            </Card.Body>
                            <style>{`
                                .hover-lift:hover {
                                    transform: translateY(-4px);
                                    box-shadow: 0 12px 24px -10px rgba(0, 0, 0, 0.08) !important;
                                    border-color: rgba(40, 176, 196, 0.3) !important;
                                }
                                .hover-lift:hover .action-btn {
                                    background-color: #28B0C4 !important;
                                }
                                .hover-lift:hover .action-btn svg {
                                    color: #ffffff !important;
                                    transform: translateX(2px);
                                }
                            `}</style>
                        </Card>
                    ))}
                </div>

                <div className="text-center pt-2">
                    <Button 
                        variant="link" 
                        className="text-decoration-none text-muted d-inline-flex align-items-center gap-2 py-2 px-3 rounded-3 hover-bg-light"
                        onClick={handleBackToLogin}
                        style={{ fontSize: '14px', transition: 'all 0.2s' }}
                    >
                        <LuLogOut size={16} /> Logout & Back to Login
                    </Button>
                </div>
            </Container>
            <ConfirmModal
                show={showLogoutModal}
                onHide={() => setShowLogoutModal(false)}
                onConfirm={confirmLogout}
                title="Confirm Logout"
                message="Are you sure you want to end your session? You will need to login again to access your portals."
                confirmText="Yes, Logout"
                variant="danger"
                icon={LuLogOut}
            />
        </div>
    );
};

export default PortalSelectBusiness;
